import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import AdminNavBar from '../components/AdminNavBar';
import { Tables } from '../types/supabase';
import withIdleTimer from '../hoc/withIdleTimer';
type AuditLog = Tables<'audit_logs'>;

const ViewAuditLog: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();
  const supabase = useSupabase();
  
  // State for audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Refs to track state and prevent duplicate operations
  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  // Filter states
  const [recordIdFilter, setRecordIdFilter] = useState<string>('');
  const [changedByFilter, setChangedByFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Handle session expiration
  useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize fetchAuditLogs to prevent recreation on each render
  const fetchAuditLogs = useCallback(async () => {
    // Skip if already fetching or no user
    if (isFetchingRef.current || !user) {
      return;
    }
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      // Start with a base query
      let query = supabase
        .from('audit_logs')
        .select('*')
        .order('changed_at', { ascending: false });
      
      // Apply filters if provided
      if (recordIdFilter) {
        query = query.ilike('record_id', `%${recordIdFilter}%`);
      }
      
      if (changedByFilter) {
        query = query.ilike('changed_by', `%${changedByFilter}%`);
      }
      
      if (startDate) {
        query = query.gte('changed_at', startDate);
      }
      
      if (endDate) {
        // Add one day to include the end date fully
        const endDateObj = new Date(endDate);
        endDateObj.setDate(endDateObj.getDate() + 1);
        const formattedEndDate = endDateObj.toISOString();
        query = query.lt('changed_at', formattedEndDate);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching audit logs:', error);
        setError(`Error fetching audit logs: ${error.message}`);
        return;
      }
      
      setAuditLogs(data as AuditLog[]);
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError(`Unexpected error: ${err.message}`);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [supabase, user, recordIdFilter, changedByFilter, startDate, endDate]);

  // Fetch audit logs when auth is ready or filters change
  useEffect(() => {
    // Update userIdRef if user changes
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }
    
    // Skip if auth is still loading
    if (authLoading) {
      return;
    }
    
    if (!user) {
      setIsLoading(false);
      return;
    }

    fetchAuditLogs();
  }, [user, authLoading, fetchAuditLogs]);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Apply filters
  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    fetchAuditLogs();
  };

  // Reset filters
  const resetFilters = () => {
    setRecordIdFilter('');
    setChangedByFilter('');
    setStartDate('');
    setEndDate('');
  };

  // Show loading state during auth loading or data fetching
  if (authLoading) {
    return (
      <div className="flex flex-col h-screen">
        <AdminNavBar user={user} onSignOut={handleSignOut} />
        <div className="flex-1 bg-gray-50 mt-16">
          <div className="p-8 text-center">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
              <span className="ml-2 text-gray-600">Verifying authentication...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <AdminNavBar user={user} onSignOut={handleSignOut} />

      {/* Main Content - Adjusted for top navbar */}
      <div className="flex-1 bg-gray-50 mt-16">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold">Audit Logs</h1>
          </div>

          {/* Filter section */}
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-lg font-semibold mb-4">Filters</h2>
            <form onSubmit={applyFilters} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="recordId" className="block text-sm font-medium text-gray-700 mb-1">
                  Record ID
                </label>
                <input
                  type="text"
                  id="recordId"
                  value={recordIdFilter}
                  onChange={(e) => setRecordIdFilter(e.target.value)}
                  placeholder="Filter by record ID"
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="changedBy" className="block text-sm font-medium text-gray-700 mb-1">
                  Changed By
                </label>
                <input
                  type="text"
                  id="changedBy"
                  value={changedByFilter}
                  onChange={(e) => setChangedByFilter(e.target.value)}
                  placeholder="Filter by user"
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="col-span-1 md:col-span-2 lg:col-span-4 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#F55D3E] text-white rounded-md hover:bg-[#e04d30]"
                >
                  Apply Filters
                </button>
              </div>
            </form>
          </div>

          {/* Error display */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Audit logs table */}
          <div className="bg-white rounded-lg shadow">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F55D3E]"></div>
                  <span className="ml-2 text-gray-600">Loading audit logs...</span>
                </div>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No audit logs found with the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Table Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Record ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changed By
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changed At
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                          {log.id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.table_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                          {log.record_id ? `${log.record_id.substring(0, 8)}...` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            log.action === 'INSERT'
                              ? 'bg-green-100 text-green-800'
                              : log.action === 'UPDATE'
                              ? 'bg-blue-100 text-blue-800'
                              : log.action === 'DELETE'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {log.changed_by || 'System'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.changed_at).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => {
                              alert(JSON.stringify(log.payload, null, 2));
                            }}
                            className="text-[#F55D3E] hover:text-[#e04d30]"
                          >
                            View Payload
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withIdleTimer(ViewAuditLog);
